/**
 * One facet chip — a LINK, not a button.
 *
 * Lifted from Great Host of Composers (`src/pages/Music.tsx`), and the reasoning
 * transfers exactly: every facet is a PLACE IN THE CATALOGUE rather than a control
 * on the page. That single choice is what makes a filtered view shareable, the
 * back button correct, and "open in new tab" meaningful — a director answering
 * "what can we sing in Advent in Common Metre" sends the URL, not a description of
 * which chips to press.
 *
 * It also makes impossible combinations UNREACHABLE rather than merely empty:
 * `facetHref` drops a pair that can hold nothing while BUILDING the link, so there
 * is no dead end to navigate to and no imperative cleanup after the fact.
 */
import { Link } from 'react-router-dom'

export function Chip({ label, href, on, count }: { label: string; href: string; on: boolean; count?: number }) {
  return (
    <Link
      to={href}
      aria-current={on ? 'true' : undefined}
      // min-h-11 is the 44px touch target the design principles require; the
      // chips wrap within their row, so a group can never strand one of its own.
      //
      // focus-visible rather than focus: a chip is a link, so a :focus ring would
      // flash on every click. The colour is the design system's own `--color-ring`,
      // which it has defined all along and nothing in this app had ever used.
      className={`inline-flex min-h-11 touch-manipulation items-center gap-1.5 rounded border px-2.5 py-1 text-xs transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none ${
        on
          ? 'border-border-strong bg-surface-raised text-foreground'
          : 'border-border text-muted-foreground hover:border-border-strong hover:text-foreground'
      }`}
    >
      {label}
      {count !== undefined && <span className="text-[0.65rem] tabular-nums opacity-70">{count}</span>}
    </Link>
  )
}

/**
 * A labelled facet row. At sm+ these are cells of ONE grid whose first column is
 * sized to the widest label, so every row keeps the same gap — a fixed width let
 * the longest label overrun its box on GHC, and any hand-picked number needs
 * revisiting the moment a label changes.
 */
export function FacetRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <span className="mt-3 self-baseline text-xs uppercase tracking-wider text-muted-foreground sm:mt-0 sm:text-right">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </>
  )
}
